import {
  MetricKpiTile,
  SectionHeading,
  panelErrorTitle,
} from "@/components/dashboard";
import { usePostLoginFbo } from "@/hooks/usePostLoginFbo";
import type { TimeRange } from "@/lib/time-range";

interface PostLoginSectionProps {
  range: TimeRange;
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}

export function PostLoginSection({ range }: PostLoginSectionProps) {
  const fbo = usePostLoginFbo(range);
  const f = fbo.data;
  const err = fbo.error ? panelErrorTitle(fbo.error) : undefined;

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="Post-login behavior"
        frs={["FR-07"]}
        source="log-analytics"
        description="How many logged-in users search or view FBO content."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MetricKpiTile
          label="FBO searches (authed)"
          value={f ? f.authedSearches.toLocaleString("en-US") : "—"}
          delta="fbo_search_submitted, isAuthenticated=true"
          tone="emerald"
          hint="FBO searches run by signed-in users."
          isLoading={fbo.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="FBO searches (anon)"
          value={f ? f.anonSearches.toLocaleString("en-US") : "—"}
          delta="fbo_search_submitted, isAuthenticated=false"
          tone="neutral"
          hint="FBO searches run by visitors who have not signed in."
          isLoading={fbo.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="Post-login search rate"
          value={fmtPct(f?.postLoginRatePct)}
          delta={
            f
              ? `${f.authedSearches} authed / ${(f.authedSearches + f.anonSearches).toLocaleString("en-US")} total`
              : undefined
          }
          tone="sky"
          hint="Share of all FBO searches that come from signed-in users."
          isLoading={fbo.isLoading}
          errorTitle={err}
        />
      </div>
    </section>
  );
}
